import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sun, BookOpen, Sparkles, PauseCircle, Check } from "lucide-react";
import api from "../lib/api";
import { useSeo } from "../lib/seo";

const OPTIONS = [
  { id: "daily_verse", icon: Sun, label: "Verse of the Day", hint: "Every morning · verse, deep dive, prompt & prayer" },
  { id: "weekly_devotional", icon: BookOpen, label: "Sunday Devotional", hint: "One honest word to carry into the week" },
  { id: "product_updates", icon: Sparkles, label: "New releases & news", hint: "First look at new ebooks, journals, stickers and sales" },
];

export default function PreferencesPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [sub, setSub] = useState(null);
  const [prefs, setPrefs] = useState([]);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  useSeo({ title: "Email preferences", description: "Choose which Faithful Store emails you receive.", path: "/preferences", noindex: true });

  useEffect(() => {
    if (!token) return setError("This link is missing its token.");
    api.get("/preferences", { params: { token } }).then((r) => { setSub(r.data); setPrefs(r.data.preferences); setPaused(!!r.data.unsubscribed); })
      .catch((e) => setError(e?.response?.data?.detail || "This link isn't valid anymore"));
  }, [token]);

  const toggle = (id) => { setSaved(false); setPrefs(prefs.includes(id) ? prefs.filter((p) => p !== id) : [...prefs, id]); };
  const save = async (pause = paused) => {
    try {
      const r = await api.put("/preferences", { token, preferences: prefs, paused: pause });
      setPaused(r.data.unsubscribed); setSaved(true);
      toast.success(r.data.unsubscribed ? "All emails paused. Come back anytime." : "Preferences saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="preferences-page">
      <div className="overline text-[#d4af37]">Email preferences</div>
      <h1 className="font-serif text-4xl text-[#0a192f] mt-3">Only what nourishes you.</h1>
      {error && <div className="card-flat p-8 mt-10 text-[#4a4a4a]" data-testid="preferences-error">{error} <Link to="/" className="underline text-[#0a192f]">Back to the store</Link></div>}
      {sub && (
        <div className="card-flat p-8 mt-10">
          <p className="text-sm text-[#4a4a4a]">Managing emails for <strong data-testid="preferences-email">{sub.email}</strong></p>
          {paused && <div className="mt-4 bg-[#fdfbf7] border border-[#d4af37]/50 px-4 py-3 text-sm text-[#0a192f] inline-flex items-center gap-2" data-testid="preferences-paused"><PauseCircle className="w-4 h-4 text-[#d4af37]" /> All emails are currently paused. Pick what you'd like and click Resume.</div>}
          <div className="mt-6 space-y-2">
            {OPTIONS.map(({ id, icon: Icon, label, hint }) => {
              const on = prefs.includes(id);
              return (
                <button key={id} data-testid={`pref-${id}`} aria-pressed={on} onClick={() => toggle(id)} className={`w-full flex items-center gap-3 text-left border p-3 transition-colors ${on ? "border-[#d4af37] bg-[#fdfbf7]" : "border-[#e5e5e5] hover:border-[#0a192f]"}`}>
                  <span className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${on ? "bg-[#0a192f] text-[#d4af37]" : "bg-[#f4f5f7] text-[#4a4a4a]"}`}><Icon className="w-4 h-4" /></span>
                  <span className="flex-1"><span className="block font-semibold text-[#0a192f] text-sm">{label}</span><span className="block text-xs text-[#4a4a4a]">{hint}</span></span>
                  <span className={`w-4 h-4 border flex-shrink-0 ${on ? "bg-[#d4af37] border-[#d4af37]" : "border-[#cfcfcf]"}`} />
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <button data-testid="preferences-save" onClick={() => save(false)} disabled={!prefs.length} className="btn-navy inline-flex items-center gap-2 disabled:opacity-50"><Check className="w-4 h-4" /> {paused ? "Resume with these" : "Save preferences"}</button>
            {!paused && <button data-testid="preferences-pause" onClick={() => save(true)} className="btn-ghost inline-flex items-center gap-2"><PauseCircle className="w-4 h-4" /> Pause all emails</button>}
          </div>
          {saved && <p className="text-xs text-emerald-700 mt-3" data-testid="preferences-saved">Saved.</p>}
          <p className="text-xs text-[#4a4a4a] mt-6">Unselecting everything pauses all emails. Purchase receipts and download links are always sent.</p>
        </div>
      )}
    </div>
  );
}
